import React, { useMemo } from "react";

const Statistics = ({ birthDate, weeksLeft, lifeProgress }) => {
  const stats = useMemo(() => {
    // Validate birthDate
    if (!birthDate || isNaN(new Date(birthDate).getTime())) {
      return {
        age: 0,
        ageExact: '0.00',
        daysLived: '0',
        weeksLived: '0',
        daysUntilBirthday: 0,
        remainingPercentage: '0.00',
        weeksPassed: '0',
        totalWeeks: '3120',
      };
    }

    const now = new Date();
    const birth = new Date(birthDate);
    
    // Calculate current age
    let age = now.getFullYear() - birth.getFullYear();
    const monthDiff = now.getMonth() - birth.getMonth();
    const dayDiff = now.getDate() - birth.getDate();
    
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }
    
    // Calculate exact age in years with decimals
    const ageInYears = (now - birth) / (1000 * 60 * 60 * 24 * 365.25);
    
    // Calculate days lived
    const daysLived = Math.floor((now - birth) / (1000 * 60 * 60 * 24));
    
    // Calculate weeks lived
    const weeksLived = Math.floor((now - birth) / (1000 * 60 * 60 * 24 * 7));
    
    // Calculate next birthday
    const nextBirthday = new Date(birth);
    nextBirthday.setFullYear(now.getFullYear());
    if (nextBirthday < now) {
      nextBirthday.setFullYear(now.getFullYear() + 1);
    }
    const daysUntilBirthday = Math.ceil((nextBirthday - now) / (1000 * 60 * 60 * 24));
    
    // Calculate remaining percentage
    const remainingPercentage = 100 - lifeProgress;
    
    // Calculate total weeks in 60 years
    const totalWeeks = 60 * 52;
    const weeksPassed = totalWeeks - weeksLeft;
    
    // Format numbers with commas
    const formatNumber = (num) => num.toLocaleString();
    
    return {
      age: Math.floor(ageInYears),
      ageExact: ageInYears.toFixed(2),
      daysLived: formatNumber(daysLived),
      weeksLived: formatNumber(weeksLived),
      daysUntilBirthday,
      remainingPercentage: remainingPercentage.toFixed(2),
      weeksPassed: formatNumber(weeksPassed),
      totalWeeks: formatNumber(totalWeeks),
    };
  }, [birthDate, weeksLeft, lifeProgress]);

  return (
    <div className="mt-16 mb-12">
      <h2 className="text-3xl font-bold text-center mb-8 text-white">
        Your Life Statistics
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Current Age */}
        <div className="bg-gradient-to-b from-gray-900 to-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
          <p className="text-gray-400 text-sm mb-2">Current Age</p>
          <p className="text-4xl font-extrabold text-white">{stats.age}</p>
          <p className="text-gray-500 text-xs mt-1">years old</p>
        </div>

        {/* Percentage Remaining */}
        <div className="bg-gradient-to-b from-gray-900 to-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
          <p className="text-gray-400 text-sm mb-2">Time Remaining</p>
          <p className="text-4xl font-extrabold text-white">{stats.remainingPercentage}%</p>
          <p className="text-gray-500 text-xs mt-1">of productive years left</p>
        </div>

        {/* Days Lived */}
        <div className="bg-gradient-to-b from-gray-900 to-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
          <p className="text-gray-400 text-sm mb-2">Days Lived</p>
          <p className="text-4xl font-extrabold text-white">{stats.daysLived}</p>
          <p className="text-gray-500 text-xs mt-1">days on Earth</p>
        </div>

        {/* Weeks Lived */}
        <div className="bg-gradient-to-b from-gray-900 to-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
          <p className="text-gray-400 text-sm mb-2">Weeks Lived</p>
          <p className="text-4xl font-extrabold text-white">{stats.weeksLived}</p>
          <p className="text-gray-500 text-xs mt-1">weeks experienced</p>
        </div>

        {/* Next Birthday */}
        <div className="bg-gradient-to-b from-gray-900 to-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
          <p className="text-gray-400 text-sm mb-2">Next Birthday</p>
          <p className="text-4xl font-extrabold text-white">{stats.daysUntilBirthday}</p>
          <p className="text-gray-500 text-xs mt-1">days away</p>
        </div>

        {/* Weeks Progress */}
        <div className="bg-gradient-to-b from-gray-900 to-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
          <p className="text-gray-400 text-sm mb-2">Weeks Passed</p>
          <p className="text-4xl font-extrabold text-white">{stats.weeksPassed}</p>
          <p className="text-gray-500 text-xs mt-1">out of {stats.totalWeeks} weeks</p>
        </div>

        {/* Life Progress Percentage */}
        <div className="bg-gradient-to-b from-gray-900 to-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
          <p className="text-gray-400 text-sm mb-2">Life Progress</p>
          <p className="text-4xl font-extrabold text-white">{lifeProgress.toFixed(2)}%</p>
          <p className="text-gray-500 text-xs mt-1">of productive years lived</p>
        </div>

        {/* Age in Exact Years */}
        <div className="bg-gradient-to-b from-gray-900 to-gray-800 p-6 rounded-lg shadow-lg border border-gray-700">
          <p className="text-gray-400 text-sm mb-2">Exact Age</p>
          <p className="text-4xl font-extrabold text-white">{stats.ageExact}</p>
          <p className="text-gray-500 text-xs mt-1">years precisely</p>
        </div>
      </div>
    </div>
  );
};

export default Statistics;

